/**
 * Raiz de la aplicacion.
 *
 * Decide entre tres mundos segun el estado de sesion — arranque, login y app — y
 * define las rutas. Las rutas de modelo son genericas a proposito: `/m/<modelo>`
 * sirve para los 500 modelos que lleguen, porque la pantalla se dibuja con la
 * metadata, no con un componente por modulo.
 *
 * `/panel` es la excepcion deliberada: la consola de administracion comparte el
 * login y el cliente de API, pero NO el shell del ERP. Se decide antes de montar
 * `AppShell` —y no como una ruta mas adentro— porque tiene armazon propio; asi
 * se puede abrir en su propia pestana del navegador y se ve otra cosa.
 *
 * Aca vivia `/portal`, una segunda excepcion que se resolvia todavia mas afuera
 * —antes del guardian de sesion— porque quien la abria no era alguien de la
 * organizacion sino el propietario del 3.º B. Se fue entera con la
 * funcionalidad de expensas: sin liquidaciones el portal no tiene nada que
 * mostrar, y con el se fue tambien la redireccion que mandaba alla a quien solo
 * tenia rol de propietario. Hoy no queda ningun rol «de afuera»: todo el que
 * entra es del equipo y aterriza en el ERP.
 */

import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AppShell } from '@/components/shell/AppShell';
import { ErrorBoundary } from '@/components/shell/ErrorBoundary';
import { HomePage } from '@/components/shell/HomePage';
import { IconSpinner } from '@/components/shell/icons';
import { LoginScreen } from '@/components/shell/LoginScreen';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import { Button } from '@/components/shell/primitives';
import { PanelApp } from '@/panel/PanelApp';
import { useSessionStore } from '@/store/session';
import { ViewSwitcher } from '@/views';
import { ActivarScreen } from '@/views/acceso/ActivarScreen';
import { CambiarClaveScreen } from '@/views/acceso/CambiarClaveScreen';
import { RecuperarScreen } from '@/views/acceso/RecuperarScreen';
import { BusquedaScreen } from '@/views/busqueda/BusquedaScreen';

/** `true` en `/panel` y en cualquier subseccion suya. */
function esRutaDelPanel(pathname: string): boolean {
  return pathname === '/panel' || pathname.startsWith('/panel/');
}

/**
 * Rutas que se abren SIN sesion.
 *
 * Se resuelven ANTES que el estado de sesion —incluso antes del splash— porque
 * quien abre `/activar` justamente todavia no puede entrar. Si pasaran por el
 * guardian normal, la persona invitada veria la pantalla de ingreso, y quien
 * tenga un token viejo en el navegador veria «tu sesion expiro»: dos formas de
 * romper el unico circuito que existe para darle acceso.
 */
const RUTAS_PUBLICAS: Record<string, () => JSX.Element> = {
  '/activar': ActivarScreen,
  '/recuperar': RecuperarScreen,
};

function Splash() {
  return (
    <div className="grid min-h-svh place-items-center bg-app" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-3 text-fg-muted">
        <span
          aria-hidden="true"
          className="grid h-11 w-11 place-items-center rounded-xl bg-accent text-lg font-bold text-accent-contrast"
        >
          N
        </span>
        <span className="flex items-center gap-2 text-sm">
          <IconSpinner className="h-4 w-4" />
          Preparando tu espacio de trabajo…
        </span>
      </div>
    </div>
  );
}

function ConnectionError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="grid min-h-svh place-items-center bg-app px-6">
      <div role="alert" className="w-full max-w-md rounded-xl border border-line bg-surface p-6 shadow-sm">
        <h1 className="text-lg font-semibold">No pudimos conectar con el servidor</h1>
        <p className="mt-2 text-sm text-fg-muted">{message}</p>
        <Button variant="primary" className="mt-5" onClick={onRetry}>
          Reintentar
        </Button>
      </div>
    </div>
  );
}

export default function App() {
  const status = useSessionStore((estado) => estado.status);
  const error = useSessionStore((estado) => estado.error);
  const bootstrap = useSessionStore((estado) => estado.bootstrap);
  const { pathname, search } = useLocation();

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // Las publicas primero: no dependen de la sesion y no deben esperarla.
  const Publica = RUTAS_PUBLICAS[pathname];
  if (Publica !== undefined) {
    return (
      <ErrorBoundary resetKey={`${pathname}${search}`}>
        <Publica />
      </ErrorBoundary>
    );
  }

  if (status === 'unknown' || status === 'loading') return <Splash />;
  if (status === 'anonymous') return <LoginScreen />;
  if (status === 'error') {
    return (
      <ConnectionError
        message={error ?? 'Error desconocido.'}
        onRetry={() => {
          void bootstrap();
        }}
      />
    );
  }

  // La consola tiene su propio armazon: no pasa por `AppShell`.
  if (esRutaDelPanel(pathname)) {
    return (
      <ErrorBoundary resetKey={`${pathname}${search}`}>
        <PanelApp />
      </ErrorBoundary>
    );
  }

  return (
    <AppShell>
      {/* La frontera envuelve SOLO el contenido: si una pantalla se cae, la
          barra lateral y la busqueda siguen funcionando y se puede ir a otra. */}
      <ErrorBoundary resetKey={`${pathname}${search}`}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* Renderer generico: la misma pantalla sirve para todos los modelos. */}
          <Route path="/m/:modelName" element={<ViewSwitcher />} />
          <Route path="/m/:modelName/:recordId" element={<ViewSwitcher />} />
          {/* La busqueda general. Ruta propia y no `/m/<modelo>` porque no es
              un modelo: cruza tres (administracion, administrador y consorcio) y
              devuelve los resultados agrupados por que son. El texto y el tipo
              viajan en la query (`?q=`, `?tipo=`) para que una busqueda se pueda
              compartir y se pueda volver atras con el navegador. */}
          <Route path="/buscar" element={<BusquedaScreen />} />
          {/* La propia contraseña: no es un modelo, es una operación sobre uno
              mismo, así que tiene ruta propia y no `/m/base.user/<id>`. */}
          <Route path="/mi-clave" element={<CambiarClaveScreen />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ErrorBoundary>
    </AppShell>
  );
}
